import {
  Button,
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import React, { useState } from "react";
import PropTypes from "prop-types";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { NODE_UI } from "../../../NodeUI/nodeUiStyles";
import "../../CredentialStoreStyle.css";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    fontSize: NODE_UI.fontSize.value,
    borderRadius: NODE_UI.borderRadiusSm,
    backgroundColor: theme.palette.background.default,
    "& fieldset": { borderColor: theme.palette.divider },
    "&:hover fieldset": { borderColor: theme.palette.text.secondary },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.text.primary,
      borderWidth: "1px",
    },
  },
  "& .MuiInputBase-input": {
    padding: NODE_UI.input.padding,
    fontSize: NODE_UI.fontSize.value,
  },
  "& .MuiFormHelperText-root": {
    fontSize: NODE_UI.fontSize.caption,
    marginLeft: 0,
    marginTop: "4px",
  },
}));

const EyeIcon = styled(IconButton)(({ theme }) => ({
  padding: "4px",
  color: theme.palette.text.secondary,
  "&:hover": {
    color: theme.palette.text.primary,
    backgroundColor: "transparent",
  },
}));

const SavedSecretState = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: NODE_UI.spacing.md,
  padding: "8px 10px",
  borderRadius: NODE_UI.borderRadiusSm,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#fafafa",
}));

const SavedSecretText = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
}));

const SecretEditHint = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
}));

const ButtonRow = styled(Box)(() => ({
  display: "flex",
  gap: NODE_UI.spacing.sm,
}));

const PasswordTextField = ({
  value,
  onChange,
  placeholder,
  errorCondition,
  errorMessage,
  fieldKey,
  credentialStatus,
  showPasswordField,
  hasInteractedWithSecret,
  onShowPasswordField,
  onInteractedWithSecret,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const shouldShowError = errorCondition && hasInteractedWithSecret;

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleConfigureSecretClick = () => {
    onShowPasswordField(true);
  };

  const handleCancelClick = () => {
    onShowPasswordField(false);
    onInteractedWithSecret(false);
    onChange(fieldKey, "");
  };

  const renderTextField = () => (
    <StyledTextField
      size="small"
      fullWidth
      type={showPassword ? "text" : "password"}
      value={value}
      variant="outlined"
      placeholder={placeholder}
      onChange={(e) => onChange(fieldKey, e.target.value)}
      error={shouldShowError}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <EyeIcon
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              edge="end"
              size="small"
            >
              {showPassword ? (
                <VisibilityOffIcon sx={{ fontSize: 16 }} />
              ) : (
                <RemoveRedEyeIcon sx={{ fontSize: 16 }} />
              )}
            </EyeIcon>
          </InputAdornment>
        ),
        autoComplete: "off",
      }}
      inputProps={{ autoComplete: "new-password" }}
      autoComplete="off"
      helperText={shouldShowError ? errorMessage : ""}
    />
  );

  if (credentialStatus === "new") {
    return renderTextField();
  }

  if (!showPasswordField) {
    return (
      <SavedSecretState>
        <SavedSecretText>
          Secret value is configured and hidden.
        </SavedSecretText>
        <Button variant="contained" size="small" onClick={handleConfigureSecretClick}>
          Update secret value
        </Button>
      </SavedSecretState>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <SecretEditHint>
        Enter a new value only if you want to replace the saved secret.
      </SecretEditHint>
      {renderTextField()}
      <ButtonRow>
        <Button variant="outlined" size="small" onClick={handleCancelClick}>
          Cancel update
        </Button>
      </ButtonRow>
    </Box>
  );
};

PasswordTextField.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  errorCondition: PropTypes.bool,
  errorMessage: PropTypes.string,
  fieldKey: PropTypes.string.isRequired,
  credentialStatus: PropTypes.string,
  showPasswordField: PropTypes.bool,
  hasInteractedWithSecret: PropTypes.bool,
  onShowPasswordField: PropTypes.func.isRequired,
  onInteractedWithSecret: PropTypes.func.isRequired,
};

export default PasswordTextField;
